/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect } from "react";
import useSWR from "swr";

import {
  receiveLADSEcoRecommendations,
  receiveLADSEcoStats,
} from "../api/lads";
import { useAccessToken } from "../auth-provider";
import { useEcoStore } from "../store/eco-store";
import { useActiveRoom } from "./use-rooms";

const REFRESH_INTERVAL = 10_000;
const REFRESH_INTERVAL_RECOMMENDATIONS = 60_000;

export type EcoRecommendationRef = {
  id: string;
  name: string;
  position: string;
};

export type EcoRecommendation = {
  type: string;
  message: string;
  rating: 1 | 2 | 3;
  devices: EcoRecommendationRef[];
};

export type EcoStats = {
  index: number;
  usage: {
    total: number;
    high: number;
    low: number;
    off: number;
    open: number;
  };
  messages: EcoRecommendation[];
};

// Removed laboperator-based eco functions - now using LADS API directly

export function useEcoStats() {
  const { activeRoom } = useActiveRoom();
  const { setError, setLoading, setStats } = useEcoStore();
  const accessToken = useAccessToken();

  // Get eco stats from LADS API
  const {
    data: ecoStats,
    error: statsError,
    isLoading: statsLoading,
  } = useSWR(
    activeRoom ? ["ecoStats", activeRoom.id, accessToken || undefined] : null,
    ([, roomId, token]) => receiveLADSEcoStats(roomId, token),
    { refreshInterval: REFRESH_INTERVAL }
  );

  // Get eco recommendations from LADS API
  const {
    data: ecoRecommendations,
    error: recommendationsError,
    isLoading: recommendationsLoading,
  } = useSWR(
    activeRoom
      ? ["ecoRecommendations", activeRoom.id, accessToken || undefined]
      : null,
    ([, roomId, token]) => receiveLADSEcoRecommendations(roomId, token),
    { refreshInterval: REFRESH_INTERVAL_RECOMMENDATIONS }
  );

  // Sync data to Zustand
  useEffect(() => {
    if (ecoStats && ecoRecommendations) {
      const stats = {
        index: (ecoStats.data as { index?: number })?.index || 0,
        usage: ecoStats.data?.usage || {
          total: 0,
          high: 0,
          low: 0,
          off: 0,
          open: 0,
        },
        messages: ecoRecommendations.data || [],
      } as EcoStats;
      setStats(stats);
      setLoading(false);
      setError(null);
    }
  }, [ecoStats, ecoRecommendations, setStats, setLoading, setError]);

  // Handle errors
  useEffect(() => {
    const combinedError = statsError || recommendationsError;
    if (combinedError) {
      setError(combinedError.message);
      setLoading(false);
    }
  }, [statsError, recommendationsError, setError, setLoading]);

  return {
    stats: {
      index: (ecoStats?.data as { index?: number })?.index || 0,
      usage: ecoStats?.data?.usage || {
        total: 0,
        high: 0,
        low: 0,
        off: 0,
        open: 0,
      },
      messages: ecoRecommendations?.data || [],
    } as EcoStats,
    error: statsError || recommendationsError,
    isLoading: statsLoading || recommendationsLoading,
  };
}
